// /**
//  * Bundle - list
//  */

// React
import React, {useContext} from 'react';
import {Link} from 'react-router-dom';
import HeaderBuilder from '../../utils/headerBuilder';
// Context
import AuthContext from '../../authContext';

// UI
import theme from '../../theme';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Badge,
  FormControlLabel,
  FormControl,
  RadioGroup,
  Radio,
  Select,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiClose,
  mdiFileDocumentOutline,
  mdiPencilOutline,
  mdiHistory,
  mdiSortBoolAscendingVariant,
  mdiCar,
} from '@mdi/js';

// Components
import Can from '../../components/Can';

// Helper
import {translateBundleStatus, checkMatchMedia} from '../../utils/helpers';
import jMoment from 'moment-jalaali';

// Type
import {Bundle} from './interfaces';

type Data = Bundle;
type Props = {
  data: any;
  handleClickOpen: (id: number) => void;
  changeStatus: any;
  handleOpenEdit: (id: number) => void;
};

type Ord = 'asc' | 'desc';

// Style
const StyledBadge = withStyles((theme: Theme) =>
  createStyles({
    badge: {
      // right: -3,
      // top: 13,
      border: `2px solid ${theme.palette.background.paper}`,
      padding: '0 4px',
      fontSize: '14px',
    },
  })
)(Badge);
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '@media (max-width: 768px)': {
        fontSize: 10,
      },
    },
    body: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: 14,
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      // minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    container: {
      maxHeight: 540,
      borderRadius: '4px',
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute',
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px',
    },
    renderValue: {
      paddingLeft: '32px',
    },
    radioGroup: {
      margin: '-8px 0',
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px',
      },
    },
  })
);

// const

const bundleStatus = [
  'CREATED',
  'ASSIGNED',
  'PROCESS_STARTED',
  'PROCESSED',
  'VEHICLE_ARRIVED',
  'LOADED',
  'PROCESSED',
  'PROCESS_FAILED',
  'CANCELLED',
];

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);

  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  const {handleClickOpen} = props;

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <TableContainer component={Paper}>
        <Table
          stickyHeader
          className={classes.table}
          aria-labelledby="tableTitle"
          size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <HeaderBuilder
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
            filterVal={props.filterVal}
            onFilterValue={(filtervalues: any) => {
              props.onFilterValue(filtervalues);
            }}
            head={props.head}
            removeFilterHandler={props.removeFilterHandler}
            haveAction={true}
            filterLabelTranslator={props.filterLabelTranslator}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow key={index}>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.driverName}
                    </StyledTableCell>
                    <StyledTableCell align="center">{row.id}</StyledTableCell>
                    <StyledTableCell align="center">
                      <StyledBadge
                        badgeContent={row.ordersCount}
                        color="secondary"
                        showZero
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.sourceName}
                    </StyledTableCell>

                    <StyledTableCell align="center">
                      {jMoment(row.deliveryDate, 'YYYY/M/D')
                        .locale('fa')
                        .format('jYY/jMM/jDD')}
                      &nbsp; &nbsp;
                      {`از  ${jMoment(row.deliveryStartTime, 'HH:mm:ss')
                        .locale('fa')
                        .format('HH:mm')}
                        `}
                      {`تا ${jMoment(row.deliveryEndTime, 'HH:mm:ss')
                        .locale('fa')
                        .format('HH:mm')}`}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {/* <NativeSelect
                          IconComponent={() => (
                            <Icon path={mdiPencilOutline} size={1} />
                          )}
                          value={row.status}
                          inputProps={{
                            name: 'name',
                            id: 'uncontrolled-native',
                          }}
                          onChange={(e: React.ChangeEvent<{value: unknown}>) =>
                            props.changeStatus({
                              id: row.id,
                              body: `${e.target.value}`,
                            })
                          }
                        >
                          <option value={`CREATED`}>
                            {translateBundleStatus('CREATED')}
                          </option>
                          <option value={`ASSIGNED`}>
                            {translateBundleStatus('ASSIGNED')}
                          </option>
                          <option value={`LOADED`}>
                            {translateBundleStatus('LOADED')}
                          </option>
                          <option value={`VEHICLE_ARRIVED`}>
                            {translateBundleStatus('VEHICLE_ARRIVED')}
                          </option>
                          <option value={`PROCESSED`}>
                            {translateBundleStatus('PROCESSED')}
                          </option>{' '}
                          <option value={`PROCESS_STARTED`}>
                            {translateBundleStatus('PROCESS_STARTED')}
                          </option>{' '}
                          <option value={`PROCESS_FAILED`}>
                            {translateBundleStatus('PROCESS_FAILED')}
                          </option>
                          <option value={`CANCELLED`} disabled>
                            {translateBundleStatus('CANCELLED')}
                          </option>
                        </NativeSelect> */}

                      <FormControl style={{width: '100%'}}>
                        <Can
                          role={authContext.user.role}
                          perform="bundles:changeStatus"
                          yes={() => (
                            <>
                              <Icon
                                path={mdiPencilOutline}
                                size={1}
                                className={classes.changStatusIcon}
                              />
                              <Select
                                className={classes.changStatusBox}
                                fullWidth
                                name={`status${index}`}
                                multiple
                                id={row.rubikaId}
                                value={[{}]}
                                IconComponent={() => null}
                                renderValue={() => (
                                  <span className={classes.renderValue}>
                                    {translateBundleStatus(row.status)}
                                  </span>
                                )}
                              >
                                <RadioGroup
                                  className={classes.radioGroup}
                                  aria-label={`status${index}`}
                                  name={`status${index}`}
                                  value={'item'}
                                  onChange={(
                                    e: React.ChangeEvent<{value: unknown}>
                                  ) =>
                                    props.changeStatus({
                                      id: row.id,
                                      body: `"${e.target.value}"`,
                                    })
                                  }
                                >
                                  {bundleStatus.map(
                                    (item: any, index: number) => (
                                      <FormControlLabel
                                        className={classes.radioGroupItem}
                                        key={index}
                                        checked={row.status === item}
                                        value={item}
                                        control={<Radio />}
                                        label={translateBundleStatus(item)}
                                      />
                                    )
                                  )}
                                </RadioGroup>
                              </Select>
                            </>
                          )}
                          no={() => (
                            <span>{translateBundleStatus(row.status)}</span>
                          )}
                        />
                      </FormControl>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <Can
                        role={authContext.user.role}
                        perform="bundles:deliverToDriver"
                        yes={() => (
                          <Link to={`/bundles/${row.id}/deliver-driver`}>
                            <IconButton
                              size="small"
                              title="تحویل مرسوله به راننده"
                            >
                              <Icon
                                color={theme.palette.blue.main}
                                path={mdiCar}
                                size={1}
                              />
                            </IconButton>
                          </Link>
                        )}
                      />

                      <Link to={`/bundles/${row.id}`}>
                        <IconButton
                          aria-label="bundle"
                          size="small"
                          title="جزئیات مرسوله"
                        >
                          <Icon
                            path={mdiFileDocumentOutline}
                            size={1}
                            color={theme.palette.green.main}
                          />
                        </IconButton>
                      </Link>

                      <Can
                        role={authContext.user.role}
                        perform="bundles:history"
                        yes={() => (
                          <Link to={`/bundles/${row.id}/allLogs`}>
                            <IconButton size="small" title="تاریخچه مرسوله">
                              <Icon path={mdiHistory} size={1} />
                            </IconButton>
                          </Link>
                        )}
                      />

                      <Can
                        role={authContext.user.role}
                        perform="bundles:edit"
                        yes={() => (
                          <Link
                            to={
                              row.status !== 'PROCESSED'
                                ? `/edit-bundle/${row.id}`
                                : '#'
                            }
                          >
                            <IconButton
                              size="small"
                              title="ویرایش سفارش های مرسوله"
                            >
                              <Icon
                                color={
                                  row.status !== 'PROCESSED'
                                    ? theme.palette.blue.main
                                    : theme.palette.green.contrastText
                                }
                                path={mdiSortBoolAscendingVariant}
                                size={1}
                              />
                            </IconButton>
                          </Link>
                        )}
                      />

                      <Can
                        role={authContext.user.role}
                        perform="bundles:edit"
                        yes={() => (
                          <IconButton
                            onClick={() => props.handleOpenEdit(row.id)}
                            size="small"
                            title="ویرایش اطلاعات راننده"
                            disabled={row.status === 'PROCESSED'}
                          >
                            <Icon
                              color={
                                row.status !== 'PROCESSED'
                                  ? theme.palette.blue.main
                                  : theme.palette.blue.contrastText
                              }
                              path={mdiPencilOutline}
                              size={1}
                            />
                          </IconButton>
                        )}
                      />

                      <Can
                        role={authContext.user.role}
                        perform="bundles:cancel"
                        yes={() => (
                          <IconButton
                            aria-label="cancele"
                            title="لغو مرسوله"
                            size="small"
                            disabled={
                              row.status === 'CANCELLED' ||
                              row.status === 'PROCESSED'
                            }
                            onClick={() => handleClickOpen(row.id)}
                          >
                            <Icon
                              path={mdiClose}
                              size={1}
                              color={
                                row.status !== 'PROCESSED'
                                  ? theme.palette.error.main
                                  : theme.palette.green.contrastText
                              }
                            />
                          </IconButton>
                        )}
                      />
                    </StyledTableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
